/**
 * Ingestion API — re-exports SDK types and utilities.
 *
 * All data-read operations route through backend proxy Lambdas. Upload operations route through the
 * upload-proxy endpoint.
 */

import type {
  RunListItem,
  GetRunResponseContent,
  DataStreamListItem,
  UpdateRunRequestContent,
  InsightItem,
} from "../generated/sdk"
import type { QuarantineRecord } from "../types/lineage"
import { sdk, sdkCall } from "./sdk"

export type IngestionRun = RunListItem
export type { QuarantineRecord }
export type DataStream = DataStreamListItem

/**
 * A single point in a record's ingestion history: either an ingested version or a quarantined
 * attempt, enriched with the source run's file name.
 */
export interface RecordTimelineEntry {
  runId: string
  timestamp: string | null
  status: "INSERTED" | "UPDATED" | "QUARANTINED"
  values: Record<string, unknown>
  reason?: string
  fileName?: string
}

interface RawHistoryEntry {
  runId: string
  timestamp?: string | null
  status?: "INSERTED" | "UPDATED"
  values: Record<string, unknown>
  reason?: string
}

/**
 * Derives a human-readable quarantine reason from a raw record's data-quality fields.
 */
function quarantineReason(record: RawHistoryEntry): string {
  const values = record.values as Record<string, unknown> | undefined
  if (values?.DataQualityEvaluationResult === "Passed") {
    return "Companion record (duplicate PK in file)"
  }
  const rulesFail = values?.DataQualityRulesFail
  if (Array.isArray(rulesFail)) {
    if (rulesFail.some((rule: string) => rule.includes("IsPrimaryKey"))) {
      return "Duplicate primary key"
    }
    if (rulesFail.length > 0) {
      const match = String(rulesFail[0]).match(/^(\w+)/)
      if (match) return `Failed: ${match[1]} check`
    }
  }
  const rawReason = record.reason || ""
  if (rawReason.includes("does not meet the constraint")) return "Data validation failed"
  return rawReason || "Validation failed"
}

async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
      })
    }
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Upload failed with status ${xhr.status}`))
    })
    xhr.addEventListener("error", () => reject(new TypeError("Network error during upload")))
    xhr.open("PUT", uploadUrl)
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream")
    xhr.send(file)
  })
}

async function waitForRunCompletion(
  studyId: string,
  runId: string,
  options: {
    maxAttempts?: number
    intervalMs?: number
    onStatusUpdate?: (run: GetRunResponseContent) => void
  } = {},
): Promise<GetRunResponseContent> {
  const { maxAttempts = 150, intervalMs = 3000, onStatusUpdate } = options
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const run = await sdk.getStudyRun({ studyId, runId })
    if (onStatusUpdate) onStatusUpdate(run)
    if (
      run.status === "COMPLETED" ||
      run.status === "FAILED" ||
      run.status === "PARTIALLY_COMPLETED"
    )
      return run
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  throw new Error("Run did not complete within timeout period")
}

export const ingestionApiService = {
  listRuns: (studyId: string, options?: { specId?: string; limit?: number; nextToken?: string }) =>
    sdk.listStudyRuns({
      studyId,
      specId: options?.specId,
      pageSize: options?.limit,
      nextToken: options?.nextToken,
    }),
  getRun: (studyId: string, runId: string) => sdk.getStudyRun({ studyId, runId }),
  updateRun: (studyId: string, runId: string, body: UpdateRunRequestContent) =>
    sdk.updateStudyRun({ studyId, runId, updateRunRequestContent: body }),
  getQuarantineData: (studyId: string, runId: string, pageSize = 50, nextToken?: string) =>
    sdk.getStudyRunQuarantineData({ studyId, runId, pageSize, nextToken }),
  exportQuarantineData: (studyId: string, runId: string) =>
    sdk.exportStudyRunQuarantineData({ studyId, runId }),
  getRunMetrics: (studyId: string) => sdk.getStudyRunMetrics({ studyId }),
  listIngestionSpecs: (studyId: string) => sdk.listStudyIngestionSpecs({ studyId }),
  listDataStreams: (studyId: string) => sdk.listStudyDataStreams({ studyId }),
  uploadFileToS3,
  waitForRunCompletion,
  listTables: (studyId: string) => sdk.listStudyTables({ studyId }),
  getTableData: (studyId: string, tableName: string, pageSize = 50, nextToken?: string) =>
    sdk.getStudyTableData({ studyId, tableName, pageSize, nextToken }),
  getInsights: async (
    studyId?: string,
    nextToken?: string,
  ): Promise<{ insights: InsightItem[]; nextToken?: string }> => {
    const data = await sdk.listStudyInsights({
      studyId: studyId ?? "all",
      nextToken,
    })
    return {
      insights: data.insights || [],
      nextToken: (data as { nextToken?: string }).nextToken,
    }
  },

  /**
   * Fetches a record's ingestion history (ingested + quarantined attempts) and enriches each entry
   * with its run's file name. Sorted newest-first.
   */
  getRecordTimeline: async (
    studyId: string,
    tableName: string,
    primaryKeyValue: string,
  ): Promise<RecordTimelineEntry[]> => {
    const { versions, quarantined } = await sdkCall(
      sdk.getRecordHistory({
        studyId,
        tableName,
        primaryKeyValue,
      }),
    )

    // Enrich with run file names, tolerating slow/failed run lookups.
    const allEntries = [...versions, ...(quarantined ?? [])]
    const runIds = [...new Set(allEntries.map((e) => e.runId))]
    const fileNames = new Map<string, string | undefined>()
    await Promise.all(
      runIds.map(async (runId) => {
        try {
          const run = await Promise.race([
            sdk.getStudyRun({ studyId, runId }),
            new Promise<null>((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000)),
          ])
          if (run) fileNames.set(runId, run.fileName)
        } catch {
          // Skip enrichment for this run.
        }
      }),
    )

    return [
      ...versions.map(
        (v): RecordTimelineEntry => ({
          runId: v.runId,
          timestamp: v.timestamp ?? null,
          status: v.status ?? "INSERTED",
          values: v.values,
          fileName: fileNames.get(v.runId),
        }),
      ),
      ...(quarantined ?? []).map(
        (q): RecordTimelineEntry => ({
          runId: q.runId,
          timestamp: q.timestamp ?? null,
          status: "QUARANTINED",
          values: q.values as Record<string, unknown>,
          reason: quarantineReason({ ...q, values: q.values as Record<string, unknown> }),
          fileName: fileNames.get(q.runId),
        }),
      ),
    ].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return tb - ta
    })
  },
}
